// Round 3 Lane 6 (R-41, R-42): the cities we have, the person's wishes for cities we don't yet, and trips across cities.
// Live cities are read straight from the public `cities` table (clients may read its public columns); wishes through the
// city function ({ wishes } / { unwish }); trips through the plan function ({ trip: { create | get } }).
// Everything fails soft: a missing table or function leaves Browse as it was.
import { supabase } from './api'
import { callFn } from './plan'
import type { Plan, PlanDay } from './plan'
import { FLAGS, useFlag, type FlagName } from './flags'

export type City = { slug: string; label: string; region: string | null; country: string | null; tz: string; places: number }
export type Wish = { city: string; label: string; created_at: string; notified_at: string | null; live: boolean }

export const LA_SLUG = 'los-angeles'
const KEY = 'mg_cities'
const FRESH_MS = 60 * 60e3

// ---------- flags: lib/flags.ts is the release lead's file; these read its lines once they are there ----------
// city_switcher (default on): the switcher on Browse and in Settings. trips (default off until 20261017106000 is applied:
// plans.trip_id must exist before a trip can be made).
const has = (n: string) => Object.prototype.hasOwnProperty.call(FLAGS, n)
function useNamedFlag(name: string, fallback: boolean): boolean {
  const known = has(name)
  const v = useFlag((known ? name : 'map_view') as FlagName) // same hook every render
  return known ? v : fallback
}
export const useCitySwitcher = () => useNamedFlag('city_switcher', true)
export const useTrips = () => useNamedFlag('trips', false)

let memo: { at: number; list: City[] } | null = null
function readKept(): { at: number; list: City[] } | null {
  try { return JSON.parse(localStorage.getItem(KEY) ?? 'null') } catch { return null }
}

/** The live cities, Los Angeles first, then by name. Kept for an hour on this device. */
export async function liveCities(): Promise<City[]> {
  const kept = memo ?? readKept()
  if (kept && Date.now() - kept.at < FRESH_MS && Array.isArray(kept.list)) return (memo = kept).list
  const { data, error } = await supabase.from('cities').select('slug,label,region,country,tz,status,places').eq('status', 'live').order('label').limit(100)
  if (error) throw new Error(error.message)
  const list = ((data ?? []) as (City & { status: string })[])
    .map(({ slug, label, region, country, tz, places }) => ({ slug, label, region, country, tz, places: Number(places ?? 0) }))
    .sort((a, b) => Number(b.slug === LA_SLUG) - Number(a.slug === LA_SLUG) || a.label.localeCompare(b.label))
  memo = { at: Date.now(), list }
  try { localStorage.setItem(KEY, JSON.stringify(memo)) } catch { /* this visit only */ }
  return list
}

/** "Lisbon, Portugal" / "San Diego, California": the label plus where it is, when that adds anything. */
export function cityLine(c: City): string {
  const where = c.country && c.country !== 'United States' ? c.country : c.region
  return where && where !== c.label ? `${c.label}, ${where}` : c.label
}

/** The zone of a city we have (Los Angeles when unknown): for the weekend window and calendar times. */
export function zoneOf(slug: string | null | undefined): string {
  const list = memo?.list ?? readKept()?.list ?? []
  return list.find((c) => c.slug === slug)?.tz ?? 'America/Los_Angeles'
}

/** The person's waiting cities (newest first), each marked live once it is ready. Read through the city function, so
 *  nothing asks the city_interest table directly (before its migration that was a 404 on every load). Visitors without
 *  a session have none, and no session is started for this. */
export async function myWishes(): Promise<Wish[]> {
  const { data: s } = await supabase.auth.getSession()
  if (!s.session) return []
  const r = await callFn<{ wishes?: Wish[] }>('city', { wishes: true })
  return Array.isArray(r.wishes) ? r.wishes : []
}

export async function dropWish(city: string): Promise<void> {
  await callFn('city', { unwish: city })
}

// ---------- trips across cities ----------
export type LegInput = { place: string; from: string; to: string; stay: string }
export type TripLeg = { plan: Plan & { dated?: boolean; trip_id?: string; dest: Plan['dest'] & { stay?: { label: string; lat: number; lng: number } | null } }; days: PlanDay[]; unscheduled: unknown[] }
export type Trip = { id: string; title: string; legs: TripLeg[] }

export const createTrip = (legs: LegInput[], title: string) =>
  callFn<{ trip: { id: string; title: string; legs: Plan[] } }>('plan', { trip: { create: { title: title.trim() || undefined, legs: legs.map((l) => ({ place: l.place.trim(), from: l.from, to: l.to, stay: l.stay.trim() || null })) } } })
export const getTrip = (id: string) => callFn<{ trip: Trip }>('plan', { trip: { get: id } })

/** Activities on a leg (dated days plus anything not placed yet). */
export const legCount = (l: TripLeg) => l.days.reduce((n, d) => n + d.items.length, 0) + l.unscheduled.length
